import { throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';

// Focus the wrapped element, keeping the browser's default scroll-into-view so
// the newly focused control stays visible for every focus-trap consumer.
function focusTrappedElement(el: HTMLElement | undefined) {
    el?.focus();
}

function getDeepActiveElement(): Element | null {
    let activeElement = document.activeElement;
    let nestedActiveElement = (activeElement as HTMLElement | null)?.shadowRoot?.activeElement;
    while (nestedActiveElement) {
        activeElement = nestedActiveElement;
        nestedActiveElement = (activeElement as HTMLElement | null)?.shadowRoot?.activeElement;
    }
    return activeElement;
}

export interface FocusResult {
    shouldPreventDefault: boolean;
}

export interface FocusTrap {
    focusNext: () => FocusResult;
    focusPrevious: () => FocusResult;
    focusFirstElement: () => void;
    // @ts-expect-error * Automated Type Error Fix
    focusElementByIndex: (index) => void;
    getFocusableElementsQuantity: () => number;
}

// @ts-expect-error * Automated Type Error Fix
export function getFocusTrap(element): FocusTrap {
    function getFocusAbleElements() {
        const allFocuseableChildren = getAllFocusAbleChildElements(element) as HTMLElement[];
        return {
            focusAbleItems: allFocuseableChildren,
            firstFocusableEl: allFocuseableChildren[0],
            lastFocusableEl: allFocuseableChildren[allFocuseableChildren.length - 1],
            elementsQuantity: allFocuseableChildren.length,
        };
    }
    return {
        focusNext() {
            const { focusAbleItems, firstFocusableEl, lastFocusableEl } = getFocusAbleElements();
            const activeElement = getDeepActiveElement();
            if (
                !activeElement ||
                !focusAbleItems.includes(activeElement as HTMLElement) ||
                activeElement === lastFocusableEl
            ) {
                focusTrappedElement(firstFocusableEl);
                return { shouldPreventDefault: true };
            }
            return { shouldPreventDefault: false };
        },
        focusPrevious() {
            const { focusAbleItems, firstFocusableEl, lastFocusableEl } = getFocusAbleElements();
            const activeElement = getDeepActiveElement();
            if (
                !activeElement ||
                !focusAbleItems.includes(activeElement as HTMLElement) ||
                activeElement === firstFocusableEl
            ) {
                focusTrappedElement(lastFocusableEl);
                return { shouldPreventDefault: true };
            }
            return { shouldPreventDefault: false };
        },
        focusFirstElement() {
            const { firstFocusableEl } = getFocusAbleElements();
            focusTrappedElement(firstFocusableEl);
        },
        focusElementByIndex(index: number) {
            const { focusAbleItems } = getFocusAbleElements();
            if (!focusAbleItems[index]) {
                throwDevelopmentOrLog(new Error(`Failed to find focusable element with index: ${index}`));
                return;
            }
            focusTrappedElement(focusAbleItems[index]);
        },
        getFocusableElementsQuantity() {
            const { elementsQuantity } = getFocusAbleElements();
            return elementsQuantity;
        },
    };
}

export async function asyncGetFocusTrap(parent: any, element: string): Promise<FocusTrap | undefined> {
    const waitForElementToLoad = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            // The drawer can already be in the DOM (e.g. auto-opened on load), in which case
            // no mutation will ever fire and the observer would wait for the timeout.
            if (parent.querySelector(element)) {
                resolve();
                return;
            }
            const observer = new MutationObserver(() => {
                const loadedElement = parent.querySelector(element);
                if (loadedElement) {
                    observer.disconnect();
                    clearObserverTimeout();
                    resolve();
                }
            });

            observer.observe(parent, { childList: true, subtree: true });

            // Timeout handling
            const timeoutId = setTimeout(() => {
                observer.disconnect();
                reject(new Error(`Timeout: ${element} not loaded.`));
            }, 5000);

            function clearObserverTimeout(): void {
                clearTimeout(timeoutId);
            }
        });
    };
    try {
        await waitForElementToLoad();
    } catch (ex) {
        throwDevelopmentOrLog(ex);
    }
    const target = parent.querySelector(element);
    return target ? getFocusTrap(target) : undefined;
}

// @ts-expect-error * Automated Type Error Fix
export function getAllFocusAbleChildElements(element, allFocuseableChildren = []) {
    const children = getDirectChildren(element, allFocuseableChildren);

    for (const child of children) {
        getAllFocusAbleChildElements(child, allFocuseableChildren);
    }

    return allFocuseableChildren;
}

// @ts-expect-error * Automated Type Error Fix
function getDirectChildren(element, allFocuseableChildren) {
    const children = [];

    // LWC components should not be focusable and if we have a shadowRoot, we are an LWC component
    if (element.shadowRoot) {
        children.push(...Array.from(element.shadowRoot.children));
    } else {
        const isSlot = element instanceof HTMLSlotElement;

        if (isSlot) {
            // If we're a slot, we may or may not be assigned elements via the slot attribute.
            // If we're assigned slot elements, then those elements replace our children, so to get those, we call the assignedElements function.
            // If we're not assigned slot elements, then we render our chilren and largely behave like any other HTML element.
            const assignedElements = Array.from(element.assignedElements() as HTMLElement[]);
            if (assignedElements.length > 0) {
                children.push(...assignedElements);
            } else if (element.children) {
                children.push(...Array.from(element.children));
            }
        } else if (element.children) {
            children.push(...Array.from(element.children));
        }

        if (element.tabIndex >= 0) {
            allFocuseableChildren.push(element);
        }
    }

    return children;
}
